import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError } from '../api/client.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { PasskeySection } from './PasskeySection.js';
import { Alert, Button, TextField } from '../ui/index.js';

/**
 * Security settings: second factor, password, and signed-in devices.
 *
 * Recovery codes are shown once, at the moment they are issued, with wording that says plainly
 * they cannot be shown again. Anything softer leads to people closing the panel and losing
 * access to every mailbox they own.
 */

function MfaSection(): ReactNode {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [enrolment, setEnrolment] = useState<{ secret: string; uri: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');

  const status = useQuery({ queryKey: ['mfa'], queryFn: () => api.get('/api/v1/auth/mfa') });

  const enrol = useMutation({
    mutationFn: () => api.post('/api/v1/auth/mfa/enrol'),
    onSuccess: (result) => setEnrolment(result),
  });

  const confirm = useMutation({
    mutationFn: () => api.post('/api/v1/auth/mfa/confirm', { code: code.trim() }),
    onSuccess: async (result) => {
      setRecoveryCodes(result.recoveryCodes);
      setEnrolment(null);
      setCode('');
      await queryClient.invalidateQueries({ queryKey: ['mfa'] });
    },
  });

  const disable = useMutation({
    mutationFn: () => api.delete('/api/v1/auth/mfa', undefined, { password }),
    onSuccess: async () => {
      setPassword('');
      setRecoveryCodes(null);
      await queryClient.invalidateQueries({ queryKey: ['mfa'] });
    },
  });

  const enabled = status.data?.enabled === true;

  return (
    <section className="card" aria-labelledby="mfa-heading">
      <h2 className="card__title" id="mfa-heading">
        {t('security.mfa.title')}
      </h2>

      <p className="status-line">
        <span
          className={`status-dot ${enabled ? 'status-dot--ok' : 'status-dot--down'}`}
          aria-hidden="true"
        />
        {enabled ? t('security.mfa.enabled') : t('security.mfa.disabled')}
      </p>

      {enabled && status.data !== undefined ? (
        <p className="empty__body">
          {t('security.mfa.recoveryRemaining', { count: status.data.recoveryCodesRemaining })}
        </p>
      ) : (
        <p className="empty__body">{t('security.mfa.explainer')}</p>
      )}

      {recoveryCodes === null ? null : (
        <Alert tone="attention" title={t('security.mfa.recoveryTitle')}>
          <p>{t('security.mfa.recoveryBody')}</p>
          <ul className="recovery-codes">
            {recoveryCodes.map((recoveryCode) => (
              <li key={recoveryCode}>
                <code>{recoveryCode}</code>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {enrolment === null ? null : (
        <div className="enrolment">
          <p className="empty__body">{t('security.mfa.scan')}</p>
          <code className="enrolment__secret">{enrolment.secret}</code>
          <form
            className="form"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              confirm.mutate();
            }}
          >
            <TextField
              label={t('auth.mfaCode')}
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              {...(confirm.error instanceof ApiError
                ? { error: t(`error.${confirm.error.code}`) }
                : {})}
            />
            <Button type="submit" variant="primary" loading={confirm.isPending}>
              {t('security.mfa.confirm')}
            </Button>
          </form>
        </div>
      )}

      {enabled ? (
        <form
          className="form"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            disable.mutate();
          }}
        >
          <TextField
            label={t('auth.currentPassword')}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            {...(disable.error instanceof ApiError
              ? { error: t(`error.${disable.error.code}`) }
              : {})}
          />
          {/* Turning off a second factor weakens the account, so it is a danger action. */}
          <Button type="submit" variant="danger" loading={disable.isPending}>
            {t('security.mfa.disable')}
          </Button>
        </form>
      ) : enrolment === null ? (
        <Button variant="primary" loading={enrol.isPending} onClick={() => enrol.mutate()}>
          {t('security.mfa.enable')}
        </Button>
      ) : null}
    </section>
  );
}

function PasswordSection(): ReactNode {
  const { t } = useI18n();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const change = useMutation({
    mutationFn: () => api.post('/api/v1/auth/password/change', { currentPassword, newPassword }),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
    },
  });

  return (
    <section className="card" aria-labelledby="password-heading">
      <h2 className="card__title" id="password-heading">
        {t('security.password.title')}
      </h2>

      {change.isSuccess ? <Alert tone="success">{t('security.password.changed')}</Alert> : null}

      <form
        className="form"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          change.mutate();
        }}
      >
        {change.error instanceof ApiError ? (
          <Alert tone="critical">{t(`error.${change.error.code}`)}</Alert>
        ) : null}

        <TextField
          label={t('auth.currentPassword')}
          type="password"
          autoComplete="current-password"
          required
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
        />
        <TextField
          label={t('auth.newPassword')}
          type="password"
          autoComplete="new-password"
          required
          minLength={12}
          hint={t('auth.register.passwordHint', { count: 12 })}
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
        />
        <Button type="submit" variant="primary" loading={change.isPending}>
          {t('security.password.change')}
        </Button>
      </form>
    </section>
  );
}

function SessionsSection(): ReactNode {
  const { t, translator } = useI18n();
  const queryClient = useQueryClient();

  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get('/api/v1/auth/sessions'),
  });

  const revoke = useMutation({
    mutationFn: (sessionId: string) =>
      api.delete('/api/v1/auth/sessions/{sessionId}', { sessionId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });

  return (
    <section className="card" aria-labelledby="sessions-heading">
      <h2 className="card__title" id="sessions-heading">
        {t('security.sessions.title')}
      </h2>

      {sessions.isPending ? <p>{t('common.loading')}</p> : null}

      <ul className="session-list" role="list">
        {(sessions.data?.items ?? []).map((session) => (
          <li key={session.id} className="session">
            <div className="session__detail">
              <span className="session__agent">{session.userAgent ?? '—'}</span>
              <span className="session__meta">
                {t('security.sessions.lastSeen', {
                  when: translator.formatRelativeTime(new Date(session.lastSeenAt)),
                })}
                {session.current ? ` · ${t('security.sessions.current')}` : ''}
              </span>
            </div>
            <Button
              variant="ghost"
              onClick={() => revoke.mutate(session.id)}
              loading={revoke.isPending && revoke.variables === session.id}
            >
              {t('security.sessions.revoke')}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function SecurityScreen(): ReactNode {
  const { t } = useI18n();

  return (
    <>
      <header className="greeting">
        <h1 className="greeting__title">{t('security.title')}</h1>
      </header>

      <PasskeySection />
      <MfaSection />
      <PasswordSection />
      <SessionsSection />
    </>
  );
}
